/**
 * CostByKeyBreakdown — the per-cost-center ("per-key") spend list.
 *
 * Grant's headline ask: "list out the cost for each key." One row per cost
 * center (respecting the page's key filter), showing calls, tokens in/out,
 * estimated cost, and a proportion bar of its share of total spend. Sortable by
 * estimated cost (default: descending).
 *
 * Reads the DB-canonical aggregate fields (est_cost_usd / input_tokens /
 * output_tokens / calls), coercing the node-pg string aggregates via `num`.
 */

import React, { useMemo, useState } from 'react';
import { Box, Typography, Skeleton, TableSortLabel, LinearProgress } from '@mui/material';
import { colors, typography } from '../../../theme/designTokens';
import { CostCenterBreakdown } from '../../../services/quillAdminService';
import { num, formatCost } from './costFormat';

export interface CostByKeyBreakdownProps {
  rows: CostCenterBreakdown[];
  totalSpend: number;
  loading?: boolean;
}

type SortDir = 'asc' | 'desc';

export const CostByKeyBreakdown: React.FC<CostByKeyBreakdownProps> = ({ rows, totalSpend, loading }) => {
  const [dir, setDir] = useState<SortDir>('desc');

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const d = num(a.est_cost_usd) - num(b.est_cost_usd);
      return dir === 'desc' ? -d : d;
    });
    return copy;
  }, [rows, dir]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={52} />
        ))}
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No cost-center spend in this range.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colors.gray[200]}`,
        borderRadius: '16px',
        overflow: 'hidden',
      }}
      data-testid="cost-by-key-breakdown"
    >
      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1.4fr 1fr 1fr', sm: '1.6fr 0.8fr 1.2fr 1fr' },
          gap: 2,
          px: 2,
          py: 1.25,
          backgroundColor: colors.gray[50],
          borderBottom: `2px solid ${colors.gray[200]}`,
        }}
      >
        <Typography sx={{ fontSize: typography.sizes.xs, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.gray[700] }}>
          Cost center (key)
        </Typography>
        <Typography sx={{ fontSize: typography.sizes.xs, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.gray[700], textAlign: 'right' }}>
          Calls
        </Typography>
        <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: typography.sizes.xs, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.gray[700], textAlign: 'right' }}>
          Tokens (in / out)
        </Typography>
        <Box sx={{ textAlign: 'right' }}>
          <TableSortLabel
            active
            direction={dir}
            onClick={() => setDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
            sx={{ '& .MuiTableSortLabel-icon': { color: `${colors.deepTeal[500]} !important` } }}
          >
            <Typography component="span" sx={{ fontSize: typography.sizes.xs, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.gray[700] }}>
              Est. cost
            </Typography>
          </TableSortLabel>
        </Box>
      </Box>

      {/* Rows */}
      {sorted.map((row, i) => {
        const cost = num(row.est_cost_usd);
        const pct = totalSpend > 0 ? (cost / totalSpend) * 100 : 0;
        return (
          <Box
            key={row.cost_center}
            sx={{
              px: 2,
              py: 1.25,
              borderTop: i === 0 ? 'none' : `1px solid ${colors.gray[100]}`,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1.4fr 1fr 1fr', sm: '1.6fr 0.8fr 1.2fr 1fr' },
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.sm, fontWeight: 600, wordBreak: 'break-word' }}>
                {row.cost_center}
              </Typography>
              <Typography sx={{ fontSize: typography.sizes.sm, textAlign: 'right' }}>
                {num(row.calls).toLocaleString()}
              </Typography>
              <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: typography.sizes.sm, textAlign: 'right', color: 'text.secondary' }}>
                {num(row.input_tokens).toLocaleString()} / {num(row.output_tokens).toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: typography.sizes.sm, fontWeight: 700, textAlign: 'right' }}>
                {formatCost(cost)}
              </Typography>
            </Box>
            {/* Proportion bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, pct))}
                sx={{
                  flexGrow: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: colors.gray[100],
                  '& .MuiLinearProgress-bar': { backgroundColor: colors.costs.main, borderRadius: 3 },
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 42, textAlign: 'right' }}>
                {pct.toFixed(1)}%
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CostByKeyBreakdown;
